import { zodResolver } from '@hookform/resolvers/zod';
import { useCopy } from '../../lib/useCopy';
import AddRounded from '@mui/icons-material/AddRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useFieldArray, useForm, useWatch, type Path } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { PageHeader } from '../../components/states';
import { useNotify } from '../../lib/notify';
import {
  attributeErrors,
  catalogFieldErrors,
  catalogKeys,
  emptyProductForm,
  productToCopyForm,
  emptyVariant,
  idempotency,
  PRODUCT_TYPES,
  productFormSchema,
  suggestSku,
  toCreateProductRequest,
  unitRowErrors,
  type Product,
  type ProductForm,
  type ProductType,
  EMPTY_SELECT_SLOTS,
} from './model';
import { PosFields } from './PosFields';
import { useAttributes, useBrands, useCategories, useUnits } from './queries';
import { CategorySelect, SectionTitle } from './ui';
import { UnitRowsEditor } from './UnitRowsEditor';
import { VariantFields } from './VariantFields';

/** /products/new — one page for the product, its variants and pack sizes. */
export function ProductFormPage() {
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const { can } = useAuth();
  const canViewCost = can('products.view_cost');
  const units = useUnits();
  const categories = useCategories();
  const brands = useBrands();
  const attributes = useAttributes();

  // Duplicate: the copied product's details, units, variants and prices (fresh code and SKUs).
  const copy = useCopy() as Product | null;
  const form = useForm<ProductForm>({
    resolver: zodResolver(productFormSchema),
    defaultValues: copy ? productToCopyForm(copy) : emptyProductForm(),
    mode: 'onBlur',
  });
  const { control, register, handleSubmit, setError, setValue, getValues, formState } = form;
  const { errors } = formState;
  const variants = useFieldArray({ control, name: 'variants' });
  const [baseUnitId, productType, tracking, code] = useWatch({
    control,
    name: ['baseUnitId', 'productType', 'trackInventory', 'code'],
  });
  const goods = productType === 'GOODS';
  const trackInventory = goods && tracking;

  // Default base unit: "Piece" when the shop has it, else the first active unit.
  const unitList = units.data;
  useEffect(() => {
    if (!unitList || getValues('baseUnitId')) return;
    const piece = unitList.find((u) => u.isActive && u.symbol.toLowerCase() === 'pc');
    const first = piece ?? unitList.find((u) => u.isActive);
    if (first) setValue('baseUnitId', first.id);
  }, [unitList, getValues, setValue]);

  const base = unitList?.find((u) => u.id === baseUnitId);
  const defs = attributes.data ?? [];
  const brandList = (brands.data ?? []).filter((b) => b.kind === 'BRAND' && b.isActive);
  const makerList = (brands.data ?? []).filter((b) => b.kind === 'MANUFACTURER' && b.isActive);

  const save = useMutation({
    mutationFn: (values: ProductForm) =>
      unwrap(
        api.POST('/products', {
          params: { header: idempotency() },
          body: toCreateProductRequest(values, defs, { canViewCost }),
        }),
      ),
    onSuccess: async (product) => {
      notify.success(`${product.name} added to the catalog`);
      await qc.invalidateQueries({ queryKey: catalogKeys.products });
      void navigate(`/products/${product.id}`, { replace: true });
    },
    onError: (e) => {
      const fieldErrors = catalogFieldErrors(e, getValues('variants'));
      for (const f of fieldErrors)
        setError(f.path as Path<ProductForm>, { type: 'server', message: f.message });
      if (fieldErrors.length === 0) notify.error(e);
    },
  });

  const submit = handleSubmit((values) => {
    // Checks zod can't do alone: unit rules depend on the base unit, attribute rules on definitions.
    let ok = true;
    for (const e of unitRowErrors(values.units, base)) {
      setError(`units.${e.index}.${e.field}`, { type: 'rule', message: e.message });
      ok = false;
    }
    values.variants.forEach((v, i) => {
      for (const [id, message] of Object.entries(attributeErrors(v.attributes, defs))) {
        setError(`variants.${i}.attributes.${id}`, { type: 'rule', message });
        ok = false;
      }
    });
    if (ok) save.mutate(values);
  });

  const fillSkus = () => {
    getValues('variants').forEach((v, i) => {
      if (!v.sku.trim()) {
        const sku = suggestSku(getValues('code'), v.nameSuffix, i);
        if (sku) setValue(`variants.${i}.sku`, sku, { shouldValidate: formState.isSubmitted });
      }
    });
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => void submit(e)}
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
          e.preventDefault();
          void submit();
        }
      }}
    >
      <Button
        size="small"
        color="inherit"
        startIcon={<ArrowBackRounded />}
        onClick={() => void navigate('/products')}
        sx={{ mb: 1.5, ml: -1, color: 'text.secondary' }}
      >
        Products
      </Button>
      <PageHeader
        title="New product"
        subtitle="Describe the item once; each size, colour or finish you sell becomes a variant with its own SKU and price."
      />

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0, 1fr) 340px' },
          alignItems: 'start',
        }}
      >
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          <Card>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <SectionTitle
                title="Basics"
                hint="What staff see when they search and on printed documents."
              />
              <Controller
                control={control}
                name="productType"
                render={({ field }) => (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Product type
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      color="primary"
                      size="small"
                      value={field.value}
                      onChange={(_, v: ProductType | null) => {
                        if (v) field.onChange(v);
                      }}
                      aria-label="Product type"
                    >
                      {PRODUCT_TYPES.map((t) => (
                        <ToggleButton key={t.value} value={t.value} sx={{ px: 2.5 }}>
                          {t.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ mt: 0.75 }}
                    >
                      {PRODUCT_TYPES.find((t) => t.value === field.value)?.hint}
                    </Typography>
                  </Box>
                )}
              />
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '2fr 1fr' },
                }}
              >
                <TextField
                  label="Product name"
                  required
                  autoFocus
                  {...register('name')}
                  error={!!errors.name}
                  helperText={errors.name?.message ?? 'e.g. Porcelain Tile'}
                />
                <TextField
                  label="Product code"
                  required
                  {...register('code', { onBlur: fillSkus })}
                  error={!!errors.code}
                  helperText={errors.code?.message ?? 'Your own short code, e.g. MT-PT'}
                />
                <TextField
                  label="Description"
                  multiline
                  minRows={2}
                  {...register('description')}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  sx={{ gridColumn: { sm: '1 / -1' } }}
                />
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <SectionTitle
                title="Variants"
                hint="One row per sellable item. A product with a single size still has one variant."
                action={
                  <Chip
                    label={`${String(variants.fields.length)} variant${variants.fields.length === 1 ? '' : 's'}`}
                    size="small"
                    variant="outlined"
                  />
                }
              />
              {errors.variants?.root?.message && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.variants.root.message}
                </Alert>
              )}
              <Stack spacing={2} divider={<Divider flexItem />}>
                {variants.fields.map((field, i) => (
                  <Box key={field.id}>
                    <Stack direction="row" sx={{ alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ flex: 1 }}>
                        Variant {i + 1}
                      </Typography>
                      <Tooltip title="Duplicate">
                        <IconButton
                          size="small"
                          aria-label={`Duplicate variant ${String(i + 1)}`}
                          onClick={() => {
                            const v = getValues(`variants.${i}`);
                            variants.insert(i + 1, { ...v, sku: '', barcode: '' });
                          }}
                        >
                          <ContentCopyRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {variants.fields.length > 1 && (
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            aria-label={`Remove variant ${String(i + 1)}`}
                            onClick={() => variants.remove(i)}
                          >
                            <DeleteOutlineRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    <VariantFields
                      control={control}
                      setValue={setValue}
                      index={i}
                      attributes={defs}
                      baseSymbol={base?.symbol ?? 'unit'}
                      trackInventory={trackInventory}
                      canPrice
                      canViewCost={canViewCost}
                      onSuggestSku={() =>
                        suggestSku(code, getValues(`variants.${i}.nameSuffix`), i)
                      }
                    />
                  </Box>
                ))}
              </Stack>
              <Button
                startIcon={<AddRounded />}
                sx={{ mt: 2 }}
                disabled={variants.fields.length >= 100}
                onClick={() => variants.append(emptyVariant())}
              >
                Add variant
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <SectionTitle
                title="Pack sizes"
                hint={`Buy or sell in bigger units; stock is always kept in ${base ? `${base.name.toLowerCase()} (${base.symbol})` : 'the base unit'}.`}
              />
              <UnitRowsEditor control={control} units={unitList ?? []} base={base} />
            </CardContent>
          </Card>
        </Stack>

        <Stack spacing={2.5} sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
          <Card>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <SectionTitle
                title="Stock unit"
                hint="Fixed after creation: stock history depends on it."
              />
              <Stack spacing={2}>
                <Controller
                  control={control}
                  name="baseUnitId"
                  render={({ field }) => (
                    <TextField
                      select
                      label="Base unit"
                      required
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.baseUnitId}
                      helperText={
                        errors.baseUnitId?.message ??
                        (base
                          ? base.allowsFraction
                            ? 'Can be sold in fractions (e.g. 2.5)'
                            : 'Whole numbers only'
                          : undefined)
                      }
                    >
                      {(unitList ?? [])
                        .filter((u) => u.isActive)
                        .map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            {u.name} ({u.symbol})
                          </MenuItem>
                        ))}
                    </TextField>
                  )}
                />
                {goods && (
                  <>
                    <Controller
                      control={control}
                      name="trackInventory"
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Track stock
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Off for items made from components when sold (see Bills of
                                materials)
                              </Typography>
                            </Box>
                          }
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="trackSerial"
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Serial numbers
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Record a serial on each sale
                              </Typography>
                            </Box>
                          }
                        />
                      )}
                    />
                  </>
                )}
                <TextField
                  label="Warranty"
                  {...register('warrantyMonths')}
                  error={!!errors.warrantyMonths}
                  helperText={errors.warrantyMonths?.message ?? 'Optional'}
                  slotProps={{
                    input: { endAdornment: <InputAdornment position="end">months</InputAdornment> },
                    htmlInput: { inputMode: 'numeric' },
                  }}
                />
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <SectionTitle title="Organise" hint="Helps filtering and reports." />
              <Stack spacing={2}>
                <Controller
                  control={control}
                  name="categoryId"
                  render={({ field }) => (
                    <CategorySelect
                      label="Category"
                      categories={categories.data}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="brandId"
                  render={({ field }) => (
                    <TextField
                      select
                      label="Brand"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={EMPTY_SELECT_SLOTS}
                    >
                      <MenuItem value="">
                        <Typography color="text.secondary">No brand</Typography>
                      </MenuItem>
                      {brandList.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  control={control}
                  name="manufacturerId"
                  render={({ field }) => (
                    <TextField
                      select
                      label="Manufacturer"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={EMPTY_SELECT_SLOTS}
                    >
                      <MenuItem value="">
                        <Typography color="text.secondary">Not set</Typography>
                      </MenuItem>
                      {makerList.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <PosFields control={control} />
                <Typography variant="caption" color="text.secondary">
                  Missing one? Add categories, brands and units in{' '}
                  <Box
                    component="a"
                    href="/products/setup"
                    target="_blank"
                    rel="noopener"
                    sx={{ color: 'primary.main', fontWeight: 600 }}
                  >
                    catalog setup
                  </Box>
                  .
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Stack direction="row" spacing={1.25} sx={{ justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => void navigate('/products')}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              loading={save.isPending}
              sx={{ flex: { xs: 1, lg: 'none' } }}
            >
              Save product
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
